import React from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiHome, 
  FiUsers, 
  FiUserPlus, 
  FiDollarSign, 
  FiTruck, 
  FiBarChart3, 
  FiSettings, 
  FiX, 
  FiCpu, 
  FiExternalLink, 
  FiShield,
  FiMessageSquare
} = FiIcons;

function Sidebar({ isOpen, onClose }) {
  const { hasPermission, user, logout } = useAuth();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: FiHome, permission: null },
    { name: 'Customers', href: '/customers', icon: FiUsers, permission: 'customers.view' },
    { name: 'Leads', href: '/leads', icon: FiUserPlus, permission: 'leads.view' },
    { name: 'Sales', href: '/sales', icon: FiDollarSign, permission: 'sales.view' },
    { name: 'Messages', href: '/messages', icon: FiMessageSquare, permission: 'customers.view' },
    { name: 'Inventory', href: '/inventory', icon: FiTruck, permission: 'inventory.view' },
    { name: 'Analytics', href: '/analytics', icon: FiBarChart3, permission: 'analytics.view' },
    { name: 'AI Tools', href: '/ai-tools', icon: FiCpu, permission: 'ai-tools.view' },
    { name: 'Users', href: '/users', icon: FiShield, permission: 'users.view' },
    { name: 'Settings', href: '/settings', icon: FiSettings, permission: 'settings.view' },
  ];

  const filteredNavigation = navigation.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-col lg:w-64 lg:bg-white lg:shadow-lg lg:border-r lg:border-gray-200">
        <div className="p-4 lg:p-6">
          <div className="flex items-center space-x-3">
            <div className="bg-primary-600 p-2 rounded-lg">
              <SafeIcon icon={FiTruck} className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg lg:text-xl font-bold text-gray-900">AutoCRM</h1>
              <p className="text-xs lg:text-sm text-gray-500">Automotive Sales</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 lg:px-4 space-y-1">
          {filteredNavigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                `group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              {({ isActive }) => (
                <motion.div
                  className="flex items-center w-full"
                  whileHover={{ x: 2 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <SafeIcon
                    icon={item.icon}
                    className={`mr-3 h-5 w-5 ${
                      isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  />
                  {item.name}
                </motion.div>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <div className="flex flex-col space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-600 font-medium text-sm">
                  {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="text-left text-sm text-gray-500 hover:text-red-600 transition-colors"
            >
              Sign Out
            </button>
            <a
              href="https://shawnryder.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 text-primary-600 hover:text-primary-700 text-sm font-medium pt-2"
            >
              <span>Powered by Shawn Ryder Digital</span>
              <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <motion.div
        initial={{ x: -300 }}
        animate={{ x: isOpen ? 0 : -300 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg border-r border-gray-200 lg:hidden"
      >
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-primary-600 p-2 rounded-lg">
              <SafeIcon icon={FiTruck} className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">AutoCRM</h1>
              <p className="text-xs text-gray-500">Automotive Sales</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {filteredNavigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={({ isActive }) =>
                `group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              {({ isActive }) => (
                <div className="flex items-center w-full">
                  <SafeIcon
                    icon={item.icon}
                    className={`mr-3 h-5 w-5 ${
                      isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  />
                  {item.name}
                </div>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <div className="flex flex-col space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-600 font-medium text-sm">
                  {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="text-left text-sm text-gray-500 hover:text-red-600 transition-colors"
            >
              Sign Out
            </button>
            <a
              href="https://shawnryder.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 text-primary-600 hover:text-primary-700 text-sm font-medium pt-2"
            >
              <span>Powered by Shawn Ryder Digital</span>
              <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
            </a>
          </div>
        </div>
      </motion.div>
    </>
  );
}

export default Sidebar;